import { BackgroundTaskBase } from "@/utils/background-tasks";
import { blue, FaroText, FontWeights, grey } from "@faro-lotv/flat-ui";
import { LinearProgress, Stack } from "@mui/material";
import { ReactNode, type JSX } from "react";

type CardProgressProps = {
  /** Label for the progress notification */
  label: ReactNode;

  /** Task to show the progress of */
  task?: BackgroundTaskBase | null;

  /** Optional sub-label to show next to the main label (e.g., remaining time) */
  subLabel?: ReactNode;

  /** Optional action to show on the right side */
  action?: ReactNode;
};

/** @returns a label or a linear progress to notify a BackgroundTask state in the upload menu card */
export function CardProgress({
  task,
  label,
  subLabel,
  action,
}: CardProgressProps): JSX.Element | null {
  if (!task) {
    return null;
  }

  return (
    <Stack sx={{ gap: 1 }}>
      <LinearProgress
        variant={task.progress > 0 ? "determinate" : "indeterminate"}
        value={task.progress}
        sx={{
          height: 4,
          borderRadius: 2,
          backgroundColor: blue[100],
          "& .MuiLinearProgress-bar": {
            backgroundColor: blue[500],
            backgroundImage: "none",
          },
        }}
      />
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
          <FaroText
            variant="helpText"
            fontWeight={FontWeights.Bold}
            color={blue[500]}
          >
            {label}
          </FaroText>
          {subLabel && (
            <FaroText
              variant="helpText"
              fontWeight={FontWeights.SemiBold}
              color={grey[700]}
            >
              {subLabel}
            </FaroText>
          )}
        </Stack>
        {action}
      </Stack>
    </Stack>
  );
}
